import React, { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Ornament } from "./Ornament";
import Seo from "./Seo";

const PROJECTS = {
  "lego-brand-activation": {
    brand: "LEGO",
    title: "Build the Future",
    kicker: "Brand Activation · Athens · 2025",
    tagline: "Building Connections. Inspiring Creativity.",
    lede:
      "An immersive brand experience that combines creativity, interactivity and aesthetics. LEGO comes to life through installations, design elements and experiential stations that encourage collaboration, imagination and play.",
    hero: "/generated/lego-arch-collage.png",
    poster: "/generated/lego-arch-collage.png",
    pillars: [
      {
        n: "01",
        t: "Immersive Environment",
        d: "Lighting, architecture and color that transport the visitor to a LEGO world from the first moment.",
      },
      {
        n: "02",
        t: "Interactive Stations",
        d: "Build zones, tactile experiences and play modules that encourage participation and co-creation.",
      },
      {
        n: "03",
        t: "Brand Engagement",
        d: "Signature displays, mascots and storytelling elements that reinforce the brand's identity and values.",
      },
      {
        n: "04",
        t: "Premium Experience",
        d: "Fine dining, lounge & networking areas — designed with a luxury sensibility for corporate guests.",
      },
    ],
    gallery: [
      "/generated/lego-brand-mockup.png",
      "/generated/lego-entrance-bar.png",
      "/generated/lego-hallway.png",
    ],
    elements: [
      "Entrance Arch & Photo Moment",
      "Interactive Build Zones",
      "Brand Displays & Installations",
      "Gifting & Takeaways",
      "Food & Beverage Experience",
      "Lounge & Networking",
    ],
    quote: {
      lines: [
        "We don't just play.",
        "We build ideas.",
        "We connect people.",
        "We create experiences.",
      ],
    },
    manifesto:
      "Every point of the experience is designed to highlight the values ​​of LEGO through modern design, creative elements and interactive moments that remain unforgettable.",
  },

  "lipton-sunsync": {
    brand: "LIPTON",
    title: "SunSync",
    kicker: "Day-to-Night Brand Experience · Athens Riviera · 2025",
    tagline: "Sync Your Summer.",
    lede:
      "SUNSYNC is an immersive summer brand experience concept that transforms Lipton's world of refreshment, optimism and social connection into a dynamic day-to-night journey. Set on the Athens Riviera, the experience evolves with the rhythm of the sun — from wellness and playful social activations to premium tea tastings, sunset rituals, music and a vibrant night-time transformation.",
    hero: "/generated/lipton-sunset-arch.png",
    poster: "/generated/lipton-sunset-arch.png",
    pillars: [
      {
        n: "01",
        t: "Day-to-Night Journey",
        d: "A dynamic experience that evolves with the sun — wellness in the morning, premium tea tastings in the afternoon, sunset rituals and live music in the evening.",
      },
      {
        n: "02",
        t: "Tea Flow Bar",
        d: "Signature bar with premium Lipton tea tastings, mocktails with real tea, natural ingredients and curated tea flights: Refresh · Detox · Focus · Boost · Balance.",
      },
      {
        n: "03",
        t: "Solar Pulse Sunset",
        d: "The signature moment of the experience — a cinematic sunset ritual with music, lighting and storytelling that synchronizes guests with nature.",
      },
      {
        n: "04",
        t: "Social Content Ecosystem",
        d: "Designed as a live event but also a social-first content platform. Every corner is a shareable moment — from the bright tunnel to the Tea Flow Bar.",
      },
    ],
    gallery: [
      "/generated/lipton-tunnel.png",
      "/generated/lipton-tea-bar.png",
      "/generated/lipton-sunset-arch.png",
    ],
    elements: [
      "Signature Entry Tunnel",
      "Tea Flow Bar & Mocktail Lab",
      "Interactive Experience Zones",
      "Solar Pulse Sunset Moment",
      "Live Music & Night Transformation",
      "Product Discovery & Sampling",
    ],
    quote: {
      lines: [
        "Tea changes everything.",
        "Good tea. Good vibes. Good people.",
        "Live bright.",
        "Live SunSync.",
      ],
    },
    manifesto:
      "Through immersive installations, the Tea Flow Bar, interactive experience zones and the signature Solar Pulse sunset moment, Lipton becomes more than a product presence — it becomes the experience itself.",
  },

  "secret-essential-oils": {
    brand: "SECRET",
    title: "Essential Oils",
    kicker: "Sensorial Influencer Experience · 2025",
    tagline: "A Sensorial Influencer Experience.",
    lede:
      "An immersive influencer event designed around the world of Secret Essential Oils. Guests journey through scent-inspired botanical environments, discover the fragrances through interactive installations, and create their own personalized blend at the Essential Oils Bar. From fragrance-inspired food and lush botanical styling to highly shareable content moments, every touchpoint transforms product discovery into a multisensory social experience.",
    hero: "/generated/secret-oils-bar.png",
    poster: "/generated/secret-oils-bar.png",
    pillars: [
      {
        n: "01",
        t: "Scent-Inspired Botanicals",
        d: "Lush botanical environments with fresh flowers, citrus, herbs and moss — a living olfactory canvas where each fragrance family gets its own corner.",
      },
      {
        n: "02",
        t: "Essential Oils Bar",
        d: "Guests create their personal blend, guided by perfumers — hands-on moments of discovery that strengthen contact with the brand's identity.",
      },
      {
        n: "03",
        t: "Interactive Installations",
        d: "Fragrance domes, scent bells and discovery stations allow influencers to feel the product before touching it — pure experiential storytelling.",
      },
      {
        n: "04",
        t: "Shareable Content Moments",
        d: "Signature backdrops, floral walls and fragrance-inspired styling every shot designed for social — content-first design that multiplies reach.",
      },
    ],
    gallery: [
      "/generated/secret-blend-bar.png",
      "/generated/secret-oils-bar.png",
      "/generated/secret-selfie-wall.png",
    ],
    elements: [
      "Botanical Entrance & Photo Wall",
      "Essential Oils Bar & Blending Lab",
      "Fragrance Discovery Domes",
      "Scent-Inspired Food Stations",
      "Influencer Content Zones",
      "Curated Gifting & Sampling",
    ],
    quote: {
      lines: [
        "Discover the scent.",
        "Blend the story.",
        "Live the fragrance.",
        "Wear your Secret.",
      ],
    },
    manifesto:
      "The event doesn't promote a product — it invites guests to experience it. Every scent becomes a moment, every moment becomes a content, every content becomes a conversation.",
  },

  "baroque-opulence": {
    brand: "BAROQUE",
    title: "Opulence Dinner Show",
    kicker: "Palazzo Visconti · Milano · September 2025",
    tagline: "Where Marie Antoinette meets modern grandeur.",
    lede:
      "An immersive dinner-show experience inspired by the court of Versailles and Rococo aesthetics. Guests are greeted by Marie-Antoinette welcome dancers, immersed in a world of fresco cherub murals, cascading florals, powdered wigs and live baroque violin, while enjoying a seven-course dinner in a setting of aristocratic opulence.",
    hero: "/generated/baroque-banquet.jpg",
    poster: "/generated/baroque-banquet.jpg",
    pillars: [
      { n: "01", t: "Marie-Antoinette Welcome", d: "Two actors in authentic peach & gold Rococo costumes welcome guests with sparkling wine coupes — a theatrical entrance that sets the tone for the evening." },
      { n: "02", t: "Fresco Cherub Set Design", d: "Hand-painted cherub cloud mural, cascading floral chandeliers, silver candelabras and banquet table full of fruit-and-flower still-life — every angle a painting." },
      { n: "03", t: "Live Baroque Ensemble", d: "A violinist in a powdered wig plays live baroque compositions in the marble hall — a cinematic soundtrack that establishes the Rococo atmosphere." },
      { n: "04", t: "Immersive Seven-Course", d: "Every dish is a moment of theater — from silver-domed reveals to smoke-filled cloches, with paired vintage wines and period-inspired plating." },
    ],
    gallery: [
      "/generated/baroque-duo.jpg",
      "/generated/baroque-banquet.jpg",
      "/generated/baroque-violinist.jpg",
    ],
    elements: [
      "Marie-Antoinette Welcome Duo",
      "Live Baroque Violin & Ensemble",
      "Cherub Fresco Backdrop",
      "Cascading Floral Installations",
      "Seven-Course Immersive Dinner",
      "Rococo Costume & Wig Styling",
    ],
    quote: {
      lines: [
        "Let them eat cake.",
        "Let them drink champagne.",
        "Let them dance until dawn.",
        "This is Baroque, reborn.",
      ],
    },
    manifesto:
      "Magnificence is detail. From the delicate lace of the costumes to every touch of the cherub mural — every element is designed like a theater stage, where guests do not watch the performance, but star in it.",
  },


  "marvis-for-kids": {
    brand: "MARVIS",
    title: "For Kids · No Ordinary Smile",
    kicker: "Fashion-Led Brand Activation · 2025",
    tagline: "No Ordinary Smile.",
    lede:
      "A playful, fashion-led brand activation created to launch a new Marvis toothpaste collection for kids. Inspired by the iconic Marvis Model, the experience transforms the brand's distinctive tubes into a living fashion installation and an interactive Toothpaste Candy Bar, where children explore different flavors through colorful product displays and playful discovery moments. Blending fashion, product storytelling and family-friendly interaction, the activation creates a highly visual environment designed for children, parents, media and creators — turning an everyday routine into 'No Ordinary Smile.'",
    hero: "/generated/marvis-dress-girl.png",
    poster: "/generated/marvis-dress-bar.png",
    pillars: [
      {
        n: "01",
        t: "The Marvis Model",
        d: "A full-length couture-style gown made entirely from original Marvis tubes — a living fashion installation that embodies the brand's iconic branding and becomes a viral photo moment.",
      },
      {
        n: "02",
        t: "Toothpaste Candy Bar",
        d: "Interactive product wall like a candy shop, where children choose flavors through colorful displays, apothecary jars with candies and playful signage \"Choose your flavor · Smile your way!\".",
      },
      {
        n: "03",
        t: "Fashion Meets Play",
        d: "Bathroom-inspired set design with pastel-mint accents, marble floor tiles, vintage sinks and fresh florals — turning an everyday routine into an editorial fashion moment.",
      },
      {
        n: "04",
        t: "Shareable Family Content",
        d: "Kid-friendly photo moments with the Marvis Model, personalized flavor discovery and custom takeaway kits — perfectly designed for parents, family media and lifestyle creators.",
      },
    ],
    gallery: [
      "/generated/marvis-dress-bar.png",
      "/generated/marvis-dress-girl.png",
    ],
    elements: [
      "Marvis Model — Toothpaste Couture Gown",
      "Toothpaste Candy Bar & Flavor Wall",
      "Interactive Product Discovery Stations",
      "Kid-Friendly Photo Moments",
      "Bathroom-Inspired Set Design",
      "Custom Takeaway Kits & Sampling",
    ],
    quote: {
      lines: [
        "Not just toothpaste.",
        "Not just fashion.",
        "Not just an event.",
        "No Ordinary Smile.",
      ],
    },
    manifesto:
      "Activation transforms an everyday habit into a moment of theater. A dress made of 500 tubes becomes couture, a toothpaste display becomes a candy shop, a brand launch becomes a family experience — because the unusual starts from the detail.",
  },
};

export default function ProjectDetailPage({ slug }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const p = PROJECTS[slug];
  if (!p) return <Navigate to="/recent-projects" replace />;

  const BASE = "https://lumiereevents.org";
  const url = `${BASE}/projects/${slug}`;
  const posterUrl = p.poster.startsWith("http") ? p.poster : BASE + p.poster;

  const eventJsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: `${p.brand} — ${p.title}`,
    description: p.lede,
    image: [posterUrl, ...(p.gallery || []).map((g) => (g.startsWith("http") ? g : BASE + g))],
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: p.kicker,
      address: {
        "@type": "PostalAddress",
        addressCountry: "GR",
        addressRegion: "Attica",
        addressLocality: "Athens",
      },
    },
    organizer: {
      "@type": "Organization",
      name: "Lumière Entertainment · Mata Canto",
      url: BASE,
      logo: `${BASE}/logo.png`,
    },
    performer: {
      "@type": "Organization",
      name: "Lumière Entertainment",
    },
    inLanguage: "el-GR",
    audience: {
      "@type": "Audience",
      audienceType: "Luxury brands, hospitality partners, corporate clients",
    },
  };

  const caseStudyJsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": url,
    name: `${p.brand} — ${p.title}`,
    headline: p.tagline,
    description: p.lede,
    image: posterUrl,
    creator: { "@type": "Organization", name: "Lumière Entertainment · Mata Canto", url: BASE },
    keywords: [
      "luxury brand experience",
      "brand activation Athens",
      "immersive brand experience Greece",
      "experiential marketing Greece",
      "brand activation Mata Canto",
      p.brand,
      p.title,
    ],
    inLanguage: ["el-GR", "en"],
    isBasedOn: BASE + "/recent-projects",
  };

  return (
    <div className="bg-[var(--lu-ivory)]" data-testid={`project-page-${slug}`}>
      <Seo
        title={`${p.brand} — ${p.title} · Recent Projects | Lumière Entertainment · Mata Canto`}
        description={p.lede}
        path={`/projects/${slug}`}
        image={p.poster}
        keywords={`${p.brand}, ${p.title}, brand activation, immersive brand experience, luxury event agency Athens, Mata Canto, Lumière Entertainment, brand activation Greece, experiential marketing Athens`}
        ogType="article"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Recent Projects", path: "/recent-projects" },
          { name: `${p.brand} — ${p.title}`, path: `/projects/${slug}` },
        ]}
        jsonLd={[eventJsonLd, caseStudyJsonLd]}
      />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden bg-[var(--lu-black)]">
        <img
          src={p.hero}
          alt={`${p.brand} ${p.title} — Lumière Brand Activation`}
          className="absolute inset-0 w-full h-full object-cover opacity-85"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--lu-black)] via-[var(--lu-black)]/40 to-transparent" />

        <div className="container-lux relative z-10 py-20 lg:py-24">
          <Link
            to="/recent-projects"
            className="inline-flex items-center gap-2 font-micro text-[10px] tracking-[0.32em] uppercase text-[var(--lu-gold)] hover:text-[var(--lu-ivory)] transition-colors"
            data-testid="back-to-recent-projects"
          >
            <ArrowLeft size={14} />
            Recent Projects
          </Link>
          <p className="kicker text-[var(--lu-gold)] mt-8">{p.kicker}</p>
          <h1
            className="mt-6 h-editorial text-[64px] sm:text-[92px] lg:text-[132px] text-[var(--lu-ivory)] leading-[0.9]"
            data-testid="project-title"
          >
            {p.brand}
          </h1>
          <p className="mt-2 h-editorial italic text-[30px] sm:text-[42px] lg:text-[54px] text-[var(--lu-gold)] leading-[1.0]">
            {p.title}
          </p>
          <p className="mt-8 max-w-2xl text-[15px] leading-[1.9] text-ivory-75 font-light">
            {p.tagline}
          </p>
        </div>
      </section>

      {/* LEDE + PILLARS */}
      <section className="section-y" data-testid="project-pillars">
        <div className="container-lux grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="kicker text-[var(--lu-gold-dark)]">The Experience</p>
            <h2 className="mt-6 h-editorial text-[36px] sm:text-[52px] text-[var(--lu-black)] leading-[1.02]">
              {p.tagline.split(".")[0]}.
              <br />
              <span className="italic text-[var(--lu-gold-dark)]">
                {p.tagline.split(".")[1]?.trim()}.
              </span>
            </h2>
            <Ornament className="my-10" />
            <p className="text-[15px] leading-[1.95] text-black-70 font-light">
              {p.lede}
            </p>
            <p className="mt-6 text-[15px] leading-[1.95] text-black-70 font-light">
              {p.manifesto}
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {p.pillars.map((pl) => (
              <article
                key={pl.n}
                className="border border-[var(--lu-gold-dark)]/25 bg-white/40 p-8 backdrop-blur-sm hover:border-[var(--lu-gold-dark)]/60 transition-colors duration-500"
                data-testid={`pillar-${pl.n}`}
              >
                <span className="font-micro text-[11px] tracking-[0.32em] text-[var(--lu-gold-dark)]">
                  {pl.n}
                </span>
                <h3 className="mt-3 h-editorial text-[24px] text-[var(--lu-black)] leading-[1.15]">
                  {pl.t}
                </h3>
                <p className="mt-3 text-[13px] leading-[1.85] text-black-70 font-light">
                  {pl.d}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section
        className="section-y bg-[var(--lu-black)] grain"
        data-testid="project-gallery"
      >
        <div className="container-lux">
          <div className="text-center">
            <p className="kicker text-[var(--lu-gold)]">Story in Frames</p>
            <h2 className="mt-6 h-editorial text-[38px] sm:text-[54px] text-[var(--lu-ivory)] leading-[1.02]">
              Inside the <span className="italic text-[var(--lu-gold)]">activation</span>
            </h2>
            <Ornament className="my-10" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-14">
            {p.gallery.map((src, i) => (
              <figure
                key={src}
                className="relative overflow-hidden border border-[var(--lu-gold)]/25"
                style={{ aspectRatio: "3 / 4" }}
                data-testid={`gallery-${i}`}
              >
                <img
                  src={src}
                  alt={`${p.brand} activation ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1400ms] hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--lu-black)]/50 to-transparent" />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCE ELEMENTS + QUOTE */}
      <section className="section-y bg-[var(--lu-cream)]" data-testid="project-elements">
        <div className="container-lux grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-6">
            <p className="kicker text-[var(--lu-gold-dark)]">Experience Elements</p>
            <h2 className="mt-6 h-editorial text-[34px] sm:text-[48px] text-[var(--lu-black)] leading-[1.02]">
              Sensory details that <span className="italic text-[var(--lu-gold-dark)]">stay with you</span>
            </h2>
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {p.elements.map((el) => (
                <li
                  key={el}
                  className="flex items-start gap-3 text-[13px] tracking-[0.14em] uppercase text-black-80 leading-[1.7]"
                >
                  <span className="mt-[6px] w-1 h-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                  {el}
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-6 lg:pl-12 lg:border-l border-[var(--lu-gold-dark)]/25">
            <p className="font-micro text-[10px] tracking-[0.32em] uppercase text-[var(--lu-gold-dark)]">
              Manifesto
            </p>
            <blockquote className="mt-6 h-editorial text-[28px] sm:text-[38px] text-[var(--lu-black)] leading-[1.15]">
              {p.quote.lines.map((line, i) => (
                <div key={i} className={i === p.quote.lines.length - 1 ? "italic text-[var(--lu-gold-dark)]" : ""}>
                  {line}
                </div>
              ))}
            </blockquote>
            <p className="mt-8 font-micro text-[10px] tracking-[0.32em] uppercase text-[var(--lu-gold-dark)]">
              Mata Canto · Luxury Experiences
            </p>
          </aside>
        </div>
      </section>

      {/* CTA */}
      <section className="section-y bg-[var(--lu-black)]">
        <div className="container-lux text-center max-w-2xl mx-auto">
          <p className="kicker text-[var(--lu-gold)]">Design your own</p>
          <h2 className="mt-6 h-editorial text-[38px] sm:text-[52px] text-[var(--lu-ivory)] leading-[1.02]">
            Let's design your own <span className="italic text-[var(--lu-gold)]">brand experience</span>.
          </h2>
          <Ornament className="my-10" />
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-gold" data-testid="project-cta-contact">
              Request Proposal
            </Link>
            <Link to="/recent-projects" className="btn-gold-outline inline-flex items-center gap-2" data-testid="project-cta-more">
              More Projects <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
